import { useMemo, useState } from 'react';
import {
  Grid, Card, Box, Affix, ActionIcon, Tooltip, Drawer,
} from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { IconPlane, IconUserPlus, IconAddressBook } from '@tabler/icons-react';
import { RoleSelector } from '../components/RoleSelector.tsx';
import { VisitorListPanel } from '../components/VisitorListPanel.tsx';
import { VisitorForm } from '../components/VisitorForm.tsx';
import { TravelDetailsPanel } from '../components/TravelDetailsPanel.tsx';
import { useHeaderSlot } from '../layout/HeaderSlot.tsx';
import { useRememberedRole } from '../hooks/useRememberedRole.ts';
import { useVisitor } from '../api/hooks/useVisitors.ts';

/**
 * Three-column workflow page with TWO responsive breakpoints, matching the
 * legacy Angular home:
 *
 *   ≥ lg (~1200px):   ┌────────┬──────────────────────┬───────────────┐
 *                     │ list   │ visitor form         │ travel details│
 *                     └────────┴──────────────────────┴───────────────┘
 *   The address-book button HIDES/SHOWS the inline left column.
 *
 *   md to <lg:        Drawer-only — left column doesn't fit inline.
 *                          ┌──────────────────────┬───────────────┐
 *                          │ visitor form         │ travel details│
 *                          └──────────────────────┴───────────────┘
 *   The address-book button TOGGLES the slide-in drawer.
 *
 *   < md (narrow):    Travel details stacks below the visitor form too.
 */
const LG_BREAKPOINT = '(min-width: 75em)';

export function HomePage() {
  const [role, setRole] = useRememberedRole();
  const [selected, setSelected] = useState<number | 'new' | null>(null);
  const [addLegSignal, setAddLegSignal] = useState(0);

  const [drawerOpened, drawer] = useDisclosure(false);
  const [columnVisible, setColumnVisible] = useState(true);
  const isWide = useMediaQuery(LG_BREAKPOINT, true);

  // Single toggle button: on wide screens collapses/restores the inline
  // column, on narrow screens opens/closes the slide-in drawer.
  const toggleVisitorList = () => {
    if (isWide) setColumnVisible(v => !v);
    else drawer.toggle();
  };

  const showInlineColumn = isWide && columnVisible;

  const buttonTooltip = isWide
    ? (columnVisible ? 'Hide visitors' : 'Show visitors')
    : (drawerOpened ? 'Close visitors' : 'Open visitors');

  useHeaderSlot({
    left: useMemo(
      () => (
        <Tooltip label={buttonTooltip} position="bottom" withArrow>
          <ActionIcon
            variant="white"
            color="blue"
            size="lg"
            radius="md"
            onClick={toggleVisitorList}
            aria-label="Toggle visitor list"
          >
            <IconAddressBook size={22} />
          </ActionIcon>
        </Tooltip>
      ),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [buttonTooltip, isWide, columnVisible, drawerOpened],
    ),
    right: useMemo(
      () => (
        <RoleSelector
          value={role}
          onChange={r => { setRole(r); setSelected(null); }}
        />
      ),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [role],
    ),
  });

  const visitorIdNumeric = typeof selected === 'number' ? selected : null;
  // Loaded here so the FAB and the travel panel both know whether the current
  // visitor is part of a group. Shares the query cache with VisitorForm.
  const loadedVisitor = useVisitor(visitorIdNumeric);
  const travelReadOnly = Boolean(loadedVisitor.data?.inGroup);

  const listPanel = (closeAfterSelect: boolean) => (
    <VisitorListPanel
      roleId={role}
      selectedId={visitorIdNumeric}
      onSelect={id => {
        setSelected(id);
        if (closeAfterSelect) drawer.close();
      }}
    />
  );

  // Centre/right Grid.Col spans expand to fill the freed space when the
  // left column isn't shown — either because we're below lg, or because the
  // user has manually collapsed it on lg+.
  const centreSpan = showInlineColumn
    ? { base: 12 as const, md: 8 as const, lg: 6 as const }
    : { base: 12 as const, md: 8 as const, lg: 8 as const };
  const rightSpan = showInlineColumn
    ? { base: 12 as const, md: 4 as const, lg: 3 as const }
    : { base: 12 as const, md: 4 as const, lg: 4 as const };

  return (
    <Box pos="relative" h="100%">
      <Drawer
        opened={drawerOpened}
        onClose={drawer.close}
        position="left"
        size="xs"
        title="Visitors"
      >
        <Box h="calc(100vh - 88px)" style={{ display: 'flex' }}>
          {listPanel(true)}
        </Box>
      </Drawer>

      <Grid gutter="sm" align="stretch">
        {showInlineColumn && (
          <Grid.Col span={3}>
            <Card withBorder padding="xs" radius="md" h="100%" mih={500}>
              {listPanel(false)}
            </Card>
          </Grid.Col>
        )}

        <Grid.Col span={centreSpan}>
          <Card withBorder padding="sm" radius="md">
            <VisitorForm
              visitorId={selected}
              ownerRoleId={role}
              onSaved={s => { if (s.id != null) setSelected(s.id); }}
              onDeleted={() => setSelected(null)}
            />
          </Card>
        </Grid.Col>

        <Grid.Col span={rightSpan}>
          <Card withBorder padding="sm" radius="md" h="100%" mih={500}>
            <TravelDetailsPanel
              visitorId={visitorIdNumeric}
              addLegSignal={addLegSignal}
              readOnly={travelReadOnly}
            />
          </Card>
        </Grid.Col>
      </Grid>

      <Affix position={{ bottom: 24, left: 24 }}>
        <Tooltip label="New visitor" position="right" withArrow>
          <ActionIcon
            size={56}
            radius="xl"
            color="blue"
            onClick={() => setSelected('new')}
            aria-label="New visitor"
          >
            <IconUserPlus size={26} />
          </ActionIcon>
        </Tooltip>
      </Affix>

      <Affix position={{ bottom: 24, right: 24 }}>
        <Tooltip
          label={
            visitorIdNumeric == null
              ? 'Pick a visitor first'
              : travelReadOnly
                ? 'Travel is managed at the group level'
                : 'Add travel'
          }
          position="left"
          withArrow
        >
          <ActionIcon
            size={56}
            radius="xl"
            color="blue"
            disabled={visitorIdNumeric == null || travelReadOnly}
            onClick={() => setAddLegSignal(n => n + 1)}
            aria-label="Add travel"
          >
            <IconPlane size={26} />
          </ActionIcon>
        </Tooltip>
      </Affix>
    </Box>
  );
}
